import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import { IOnInventoryAuditDTO } from "../models/DTO/IInventoryAuditDTO";
import IGetReservedOrdersDTO from "../models/DTO/IGetReservedOrdersDTO";
import IOnGetReservedOrdersDTO from "../models/DTO/IOnGetReservedOrdersDTO";
import IOnGetBackorderedOrdersDTO from "../models/DTO/IOnGetBackorderedOrdersDTO";
import IOnGetIncomingPurchaseOrdersDTO from "../models/DTO/IOnGetIncomingPurchaseOrdersDTO";
import IOnGetInventoryLogsDTO from "../models/DTO/IOnGetInventoryLogsDTO";
import IOnGetInProductionBreakdownDTO from "../models/DTO/IOnGetInProductionBreakdownDTO";
import IOnGetInProductionAllocationDTO from "../models/DTO/IOnGetInProductionAllocationDTO";
import IGetInventoryByCustomerDTO from "../models/DTO/IGetInventoryByCustomerDTO";
import IOnGetInventoryByCustomerDTO from "../models/DTO/IOnGetInventoryByCustomerDTO";
import IGetSingleOrderBackorderAnalysisDTO from "../models/DTO/IGetSingleOrderBackorderAnalysisDTO";
import IOnGetSingleOrderBackorderAnalysisDTO from "../models/DTO/IOnGetSingleOrderBackorderAnalysisDTO";
import InventoryAllocationStrategyEnum from "../models/DTO/InventoryAllocationStrategyEnum";
import { IFulfillBackordersByOrderDTO, IFulfillBackordersByOrderResultDTO } from "../models/DTO/IFulfillBackordersByOrderDTO";
import { IInventoryResetDTO, IOnInventoryResetDTO } from "../models/DTO/IInventoryResetDTO";

interface InventoryAuditApiResponse {
    success: boolean;
    message?: string;
    data?: IOnInventoryAuditDTO;
}

/**
 * Audit reserved quantity on hand for discrepancies
 * Checks catalog items for inventory reservation discrepancies without fixing them
 */
const auditInventory = async (
    catalogGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<InventoryAuditApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryAudit,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ catalogGuid }),
    });

    if (!response.ok) {
        throw new Error(`Failed to audit inventory: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Audit and fixreserved quantity on hand for discrepancies
 * Checks catalog items for inventory reservation discrepancies without fixing them
 */
const auditAndFixInventory = async (
    catalogGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<InventoryAuditApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryAuditFix,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ catalogGuid }),
    });

    if (!response.ok) {
        throw new Error(`Failed to audit inventory: ${response.statusText}`);
    }

    return response.json();
};

interface GetReservedOrdersApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetReservedOrdersDTO;
    totalCount?: number;
}

interface GetReservedOrdersCsvApiResponse {
    success: boolean;
    message?: string;
    csvData?: string;
    filename?: string;
}

/**
 * Get paginated list of orders with reserved quantity for a catalog item
 */
const getReservedOrders = async (
    params: IGetReservedOrdersDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetReservedOrdersApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryReservedOrders,
        params: { catalogItemGuid: params.catalogItemGuid },
        query: {
            limit: params.limit || 25,
            page: params.page || 0,
            ...(params.vendorGuid ? { vendorGuid: params.vendorGuid } : {}),
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get reserved orders: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Export all orders with reserved quantity as CSV
 */
const getReservedOrdersCsv = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorGuid?: string
): Promise<GetReservedOrdersCsvApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryReservedOrdersCsv,
        params: { catalogItemGuid },
        query: {
            ...(vendorGuid ? { vendorGuid } : {}),
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to export reserved orders: ${response.statusText}`);
    }

    return response.json();
};

interface GetBackorderedOrdersParams {
    catalogItemGuid: string;
    limit?: number;
    offset?: number;
    vendorGuid?: string;
}

interface GetBackorderedOrdersApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetBackorderedOrdersDTO;
    totalCount?: number;
}

interface GetBackorderedOrdersCsvApiResponse {
    success: boolean;
    message?: string;
    csvData?: string;
    filename?: string;
}

/**
 * Get paginated list of orders with backordered quantity for a catalog item
 */
const getBackorderedOrders = async (
    params: GetBackorderedOrdersParams,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetBackorderedOrdersApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryBackorderedOrders,
        params: { catalogItemGuid: params.catalogItemGuid },
        query: {
            limit: params.limit || 25,
            page: params.offset || 0,
            ...(params.vendorGuid ? { vendorGuid: params.vendorGuid } : {}),
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get backordered orders: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Export all orders with backordered quantity as CSV
 */
const getBackorderedOrdersCsv = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorGuid?: string
): Promise<GetBackorderedOrdersCsvApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryBackorderedOrdersCsv,
        params: { catalogItemGuid },
        query: {
            ...(vendorGuid ? { vendorGuid } : {}),
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to export backordered orders: ${response.statusText}`);
    }

    return response.json();
};

interface GetIncomingPurchaseOrdersParams {
    catalogItemGuid: string;
    limit?: number;
    offset?: number;
}

interface GetIncomingPurchaseOrdersApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetIncomingPurchaseOrdersDTO;
    totalCount?: number;
}

interface GetIncomingPurchaseOrdersCsvApiResponse {
    success: boolean;
    message?: string;
    csvData?: string;
    filename?: string;
}

/**
 * Get paginated list of open (Incoming state) purchase orders with outstanding quantity for a catalog item
 */
const getIncomingPurchaseOrders = async (
    params: GetIncomingPurchaseOrdersParams,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetIncomingPurchaseOrdersApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryIncomingPurchaseOrders,
        params: { catalogItemGuid: params.catalogItemGuid },
        query: {
            limit: params.limit || 25,
            page: params.offset || 0,
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get incoming purchase orders: ${response.statusText}`);
    }

    return response.json();
};

/**
 * Export all open purchase orders with outstanding quantity as CSV
 */
const getIncomingPurchaseOrdersCsv = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetIncomingPurchaseOrdersCsvApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryIncomingPurchaseOrdersCsv,
        params: { catalogItemGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to export incoming purchase orders: ${response.statusText}`);
    }

    return response.json();
};

interface GetInventoryByCustomerApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetInventoryByCustomerDTO;
    totalCount?: number;
}

/**
 * Get paginated per-SKU reserved and backordered quantities for a single customer
 */
const getInventoryByCustomer = async (
    params: IGetInventoryByCustomerDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetInventoryByCustomerApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryByCustomer,
        params: { vendorGuid: params.vendorGuid },
        query: {
            limit: params.limit || 25,
            page: params.page || 0,
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get inventory by customer: ${response.statusText}`);
    }

    return response.json();
};

interface GetInventoryByCustomerCsvApiResponse {
    success: boolean;
    message?: string;
    csvData?: string;
    filename?: string;
}

/**
 * Export a customer's per-SKU reserved and backordered quantities as CSV
 */
const getInventoryByCustomerCsv = async (
    vendorGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetInventoryByCustomerCsvApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryByCustomerCsv,
        params: { vendorGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to export inventory by customer: ${response.statusText}`);
    }

    return response.json();
};

interface GetSingleOrderBackorderAnalysisApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetSingleOrderBackorderAnalysisDTO;
}

/**
 * Get backorder analysis for a single order
 * Analyzes fulfillment potential for a specific order's backordered items
 */
const getSingleOrderBackorderAnalysis = async (
    params: IGetSingleOrderBackorderAnalysisDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetSingleOrderBackorderAnalysisApiResponse> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventorySingleOrderBackorderAnalysis,
        params: { orderGuid: params.orderGuid },
        query: {
            allocationStrategy: params.allocationStrategy || InventoryAllocationStrategyEnum.FIFO,
            considerReservedInventory: params.considerReservedInventory || false,
        },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get backorder analysis: ${response.statusText}`);
    }

    return response.json();
};

interface FulfillBackordersByOrderApiResponse {
    success: boolean;
    message?: string;
    data?: IFulfillBackordersByOrderResultDTO;
}

/**
 * Fulfill backorders for specific orders
 * Allocates inventory to pending order items based on allocation strategy
 */
const fulfillBackordersByOrder = async (
    payload: IFulfillBackordersByOrderDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<FulfillBackordersByOrderApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryFulfillBackordersByOrder,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`Failed to fulfill backorders: ${response.statusText}`);
    }

    return response.json();
};

interface InventoryResetApiResponse extends IOnInventoryResetDTO {}

/**
 * Reset inventory reservations for a catalog
 * Implements the "Reconstruct & Pour" strategy
 */
const resetInventory = async (
    payload: IInventoryResetDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<InventoryResetApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryReset,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`Failed to reset inventory: ${response.statusText}`);
    }

    return response.json();
};

interface GetInProductionBreakdownApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetInProductionBreakdownDTO;
}

/**
 * Get per-workflow-step counts of a catalog item's WIP serials (the In Production column drill-in)
 */
const getInProductionBreakdown = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetInProductionBreakdownApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryInProduction,
        params: { catalogItemGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get in-production breakdown: ${response.statusText}`);
    }

    return response.json();
};

interface GetInProductionAllocationApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetInProductionAllocationDTO;
}

/**
 * Get the unassigned WIP serial pool and waiting order lines for the In Production earmark flow
 */
const getInProductionAllocation = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<GetInProductionAllocationApiResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.InventoryInProductionAllocation,
        params: { catalogItemGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to get in-production allocation: ${response.statusText}`);
    }

    return response.json();
};

interface GetInventoryLogsApiResponse {
    success: boolean;
    message?: string;
    data?: IOnGetInventoryLogsDTO;
    totalCount?: number;
}

/** Thrown when the item isn't in this supplier's catalog (404), so the page can say so instead of a generic error. */
export class InventoryLogItemNotFoundError extends Error {}

/** Thrown when the user's role may not read inventory logs (403). */
export class InventoryLogAccessError extends Error {}

/**
 * One catalog item's inventory log, newest first, one page at a time (OR-1555).
 * `filters` are the inventory log FilterBar's query (date_from, date_to, operation, order, po, warehouse_guid, note)
 * plus the order_guid / po_guid scope from an order or PO line link.
 */
const getInventoryLogs = async (
    params: { catalogItemGuid: string; page: number; limit: number; filters: Record<string, unknown> },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IOnGetInventoryLogsDTO> => {
    const query: Record<string, string | number> = { limit: params.limit, page: params.page };

    for (const [key, value] of Object.entries(params.filters)) {
        if (value !== undefined && value !== null && value !== "") {
            query[key] = Array.isArray(value) ? value.join(",") : String(value);
        }
    }

    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.InventoryLogs,
        params: { catalogItemGuid: params.catalogItemGuid },
        query,
    });

    const response = await fetch(url, { method: "GET", headers: buildAuthHeadersForFetch({ userModel, supplierModel }) });

    if (response.status === 404) {
        throw new InventoryLogItemNotFoundError("Catalog item not found");
    }

    if (response.status === 403) {
        throw new InventoryLogAccessError("Not allowed to view inventory logs");
    }

    const body: GetInventoryLogsApiResponse = await response.json().catch(() => ({ success: false }));

    if (!response.ok || !body.success || !body.data) {
        throw new Error(body.message || `Failed to get inventory logs: ${response.statusText}`);
    }

    return body.data;
};

const InventoryService = {
    auditInventory,
    auditAndFixInventory,
    getReservedOrders,
    getReservedOrdersCsv,
    getBackorderedOrders,
    getBackorderedOrdersCsv,
    getIncomingPurchaseOrders,
    getIncomingPurchaseOrdersCsv,
    getInventoryLogs,
    getInProductionBreakdown,
    getInProductionAllocation,
    getInventoryByCustomer,
    getInventoryByCustomerCsv,
    getSingleOrderBackorderAnalysis,
    fulfillBackordersByOrder,
    resetInventory,
};

export default InventoryService;
